import { Feature, FeatureCollection, GeoJsonObject, Geometry, Position } from 'geojson'
import { LatLngBounds } from 'leaflet'

/**
 * The box a map has to show to hold a project's document.
 *
 * The selection is the backend's, not one of our own: `BOX_FEATURES` in
 * `app/models/concerns/geojson_geometry.rb` is `NOT_DECORATION || CARRIES_IMAGE`, and the
 * description of `Project.boundingBox` reads "enclosing every feature of the project's geojson
 * except decoration that carries no `image`". When that rule moves, it moves there first, and this
 * file follows.
 *
 * It is deliberately **not** the selection `geojsonCenter` uses. Where a project *is* and what a
 * map has to *show* are two questions: a rectangle drawn in the CMS for a picture arrives as
 * `deko: true` with an `image`, it says nothing about where the project lies, and leaving it out of
 * the box would cut its picture off.
 *
 * Read from the document rather than from the drawn layers. A feature carries its own zoom range,
 * so what is drawn depends on the zoom — and the zoom is what a fit sets. A box taken from the
 * layers would decide its own input.
 */

// Decoration is the literal `true` and nothing else, the way the backend's jsonpath reads it: a
// document that writes its booleans as `"false"` would otherwise shrink a box without a word.
const isDecoration = (feature: Feature): boolean =>
  (feature.properties ?? {}).deko === true

// A carrier the way the backend counts one: a string that is not empty, and nothing else about
// it. Word for word `CARRIES_IMAGE`, whitespace included — a rule copied from another repository
// is only worth having while it is the same rule.
//
// Narrower than the frontend's `overlayImageUrl`, which also insists on http or https: a carrier
// naming an address no browser can resolve is in the box here and draws nothing. That is the
// price of holding one rule instead of two, and of the two ways to be wrong the box is the
// forgiving one.
const carriesImage = (feature: Feature): boolean => {
  const raw = (feature.properties ?? {}).image
  return typeof raw === 'string' && raw !== ''
}

const featuresOf = (geojson: GeoJsonObject): Feature[] => {
  if (geojson.type === 'FeatureCollection') {
    return (geojson as FeatureCollection).features
  }
  if (geojson.type === 'Feature') {
    return [geojson as Feature]
  }
  // A bare geometry, which instances deliver until a document has been saved once. It carries no
  // properties, so nothing can take it out of the box.
  return []
}

const eachPosition = (coordinates: unknown, visit: (position: Position) => void): void => {
  if (!Array.isArray(coordinates)) {
    return
  }
  if (typeof coordinates[0] === 'number') {
    visit(coordinates as unknown as Position)
    return
  }
  coordinates.forEach((part) => eachPosition(part, visit))
}

const eachGeometryPosition = (
  geometry: Geometry | null | undefined,
  visit: (position: Position) => void
): void => {
  if (!geometry) {
    return
  }
  // A GeometryCollection carries `geometries` instead of `coordinates`; without this a project
  // made of one would have no box at all.
  if (geometry.type === 'GeometryCollection') {
    geometry.geometries.forEach((part) => eachGeometryPosition(part, visit))
    return
  }
  if ('coordinates' in geometry) {
    eachPosition(geometry.coordinates, visit)
  }
}

/**
 * The box one document needs, or nothing where it has no usable coordinates.
 *
 * Where the selection leaves nothing — a document made only of decoration without a picture, for
 * which the backend has no box either — every feature counts. A project that is in the list should
 * be on the map, and half a rule would leave it outside the fit.
 */
export const geojsonBounds = (geojson?: GeoJsonObject): LatLngBounds | undefined => {
  if (!geojson) {
    return undefined
  }

  const features = featuresOf(geojson)
  const inBox = features.filter((feature) => !isDecoration(feature) || carriesImage(feature))
  const considered = inBox.length > 0 ? inBox : features

  let minLat = Number.POSITIVE_INFINITY
  let maxLat = Number.NEGATIVE_INFINITY
  let minLng = Number.POSITIVE_INFINITY
  let maxLng = Number.NEGATIVE_INFINITY

  const visit = (position: Position): void => {
    const [lng, lat] = position
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return
    }
    minLat = Math.min(minLat, lat)
    maxLat = Math.max(maxLat, lat)
    minLng = Math.min(minLng, lng)
    maxLng = Math.max(maxLng, lng)
  }

  if (considered.length > 0) {
    considered.forEach((feature) => eachGeometryPosition(feature.geometry, visit))
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    eachGeometryPosition(geojson as any, visit)
  }

  if (minLat > maxLat) {
    return undefined
  }

  return new LatLngBounds([minLat, minLng], [maxLat, maxLng])
}

/**
 * The box that holds every document handed in, or nothing where none of them has one.
 *
 * Built by extending one box rather than by collecting corners, so a document without coordinates
 * simply adds nothing.
 */
export const boundsOfAll = (documents: Array<GeoJsonObject | undefined>): LatLngBounds | undefined =>
  documents.reduce<LatLngBounds | undefined>((all, document) => {
    const one = geojsonBounds(document)
    if (!one) {
      return all
    }
    return all ? all.extend(one) : one
  }, undefined)
